import { useEffect, useMemo, useState } from "react";
import { api, fmtCurrency } from "@/lib/api";
import { useCapital } from "@/context/CapitalContext";
import { TID } from "@/constants/testIds";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/common/SearchableSelect";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { AssetDecisionDialog, DecisionBadge } from "@/components/capital/AssetDecisionDialog";
import { Search, Info as InfoIcon, Loader2, QrCode, Building2, ImageDown, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ClientPostersDialog } from "@/components/capital/ClientPostersDialog";
import { TagLogoSettingsDialog } from "@/components/capital/TagLogoSettingsDialog";
import { Settings2 } from "lucide-react";
import { toast } from "sonner";
import { displayStatus, CONSEQ, RATING, ConsequenceBadge } from "@/lib/capitalLabels";

const HORIZON_STYLE = {
  "Immediate Attention": "bg-rose-50 text-rose-700 border-rose-200",
  "12 Month Plan": "bg-amber-50 text-amber-700 border-amber-200",
  "24 Month Plan": "bg-blue-50 text-gbe border-blue-200",
  "Monitor": "bg-slate-100 text-slate-600 border-slate-200",
};
const COND_COLOR = (c) => {
  if (c >= 5) return "bg-rose-600 text-white";
  if (c >= 4) return "bg-rose-500 text-white";
  if (c >= 3) return "bg-amber-500 text-white";
  if (c >= 2) return "bg-emerald-500 text-white";
  return "bg-emerald-600 text-white";
};

const ALL_HORIZONS = ["Immediate Attention", "12 Month Plan", "24 Month Plan", "Monitor"];

export function RenewalAssessment({ fixedHorizon = null, title = null, tone = null }) {
  const { client } = useCapital();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [horizon, setHorizon] = useState(fixedHorizon || "all");
  const [group, setGroup] = useState("all");
  const [site, setSite] = useState("all");
  const [printingLabels, setPrintingLabels] = useState(false);
  const [logoSettingsOpen, setLogoSettingsOpen] = useState(false);
  const [clientQr, setClientQr] = useState(false);
  const [clientQrImg, setClientQrImg] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [sort, setSort] = useState("priority");
  const [filters, setFilters] = useState({ asset_groups: [], sites: [] });
  const [selected, setSelected] = useState(null);
  const [decisionAsset, setDecisionAsset] = useState(null);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  useEffect(() => {
    if (!client) return;
    api.get("/capital/filters", { params: { client } }).then((r) => setFilters(r.data));
  }, [client]);

  useEffect(() => { setPage(0); }, [q, horizon, group, site, sort, fixedHorizon]);

  useEffect(() => {
    if (!client) return;
    setLoading(true);
    const params = { client, limit: PAGE_SIZE, skip: page * PAGE_SIZE, sort };
    if (fixedHorizon) params.horizon = fixedHorizon;
    else if (horizon !== "all") params.horizon = horizon;
    if (group !== "all") params.asset_group = group;
    if (site !== "all") params.site = site;
    if (q) params.q = q;
    const c = setTimeout(() => {
      api.get("/capital/assets", { params })
        .then((r) => { setItems(r.data.items); setTotal(r.data.total); })
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(c);
  }, [client, q, horizon, group, site, sort, page, fixedHorizon]);

  const heading = title || "Renewal Assessment";
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="p-4 flex flex-wrap items-center gap-3 border-b border-slate-100">
          <div className="mr-auto">
            <h3 className="font-display text-lg font-semibold text-slate-900">{heading}</h3>
            <p className="text-xs text-slate-500">
              {loading ? "Loading…" : `${total} assets · sorted by ${sort === "priority" ? "priority score" : sort}`}
            </p>
          </div>

          <div className="relative w-[260px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid={TID.capitalSearch}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search asset, site, type…"
              className="pl-9 h-9 bg-slate-50 border-slate-200"
            />
          </div>

          {!fixedHorizon && (
            <Select value={horizon} onValueChange={setHorizon}>
              <SelectTrigger data-testid={TID.capitalHorizonFilter} className="w-[170px] h-9">
                <SelectValue placeholder="Horizon" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All horizons</SelectItem>
                {ALL_HORIZONS.map((h) => (<SelectItem key={h} value={h}>{displayStatus(h)}</SelectItem>))}
              </SelectContent>
            </Select>
          )}

          <SearchableSelect
            value={group} onChange={setGroup}
            options={filters.asset_groups.map((g) => ({ value: g, label: g }))}
            allLabel="All asset classes" placeholder="Asset class"
            width="w-[200px]" testid={TID.capitalGroupFilter}
          />

          <SearchableSelect
            value={site} onChange={setSite}
            options={filters.sites.map((s) => ({ value: s, label: s }))}
            allLabel="All sites" placeholder="Site"
            width="w-[240px]" testid={TID.capitalSiteFilter}
          />

          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[160px] h-9">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="priority">Priority score ↓</SelectItem>
              <SelectItem value="rank">Portfolio rank ↑</SelectItem>
              <SelectItem value="cost">Cost ↓</SelectItem>
              <SelectItem value="remaining_life">Remaining life ↑</SelectItem>
            </SelectContent>
          </Select>

          <Button size="sm" variant="outline" className="h-9 text-xs" disabled={printingLabels}
                  data-testid="print-labels-btn"
                  title={site === "all" ? "Select a site to print its labels, or print all (max 500)" : `Print labels for ${site}`}
                  onClick={async () => {
                    if (site === "all" && !window.confirm("No site selected — print labels for ALL sites (max 500 assets)?")) return;
                    setPrintingLabels(true);
                    try {
                      const params = new URLSearchParams({ client });
                      if (site !== "all") params.set("site", site);
                      const r = await api.get(`/field/labels.pdf?${params.toString()}`, { responseType: "blob", timeout: 180000 });
                      const u = URL.createObjectURL(r.data);
                      const el = document.createElement("a");
                      el.href = u;
                      el.download = `qr_labels_${(site !== "all" ? site : client).replace(/[^a-z0-9]+/gi, "_").slice(0, 40)}.pdf`;
                      el.click();
                      URL.revokeObjectURL(u);
                    } catch { toast.error("Label generation failed"); }
                    finally { setPrintingLabels(false); }
                  }}>
            {printingLabels ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <QrCode className="w-3.5 h-3.5 mr-1" />}
            Print QR labels{site !== "all" ? " (site)" : ""}
          </Button>

          <Button size="sm" variant="ghost" className="h-9 px-2 text-xs text-slate-500"
                  data-testid="tag-logo-settings-btn" title="Choose which clients get the GBE logo on their tags"
                  onClick={() => setLogoSettingsOpen(true)}>
            <Settings2 className="w-3.5 h-3.5 mr-1" /> Tag logo
          </Button>
          <TagLogoSettingsDialog open={logoSettingsOpen} onClose={() => setLogoSettingsOpen(false)} />

          <Button size="sm" variant="outline" className="h-9 text-xs" disabled={clientQr || !client}
                  data-testid="client-qr-btn"
                  title={client ? `Download the whole-of-client QR poster for ${client}` : "Select a client first"}
                  onClick={async () => {
                    if (!client) return;
                    setClientQr(true);
                    try {
                      const params = new URLSearchParams({ client_name: client });
                      const r = await api.get(`/site-qr/client-poster.pdf?${params.toString()}`, { responseType: "blob", timeout: 120000 });
                      const u = URL.createObjectURL(r.data);
                      const el = document.createElement("a");
                      el.href = u;
                      el.download = `GBE-Client-QR-${client.replace(/[^a-z0-9]+/gi, "_").slice(0, 40)}.pdf`;
                      el.click();
                      URL.revokeObjectURL(u);
                    } catch { toast.error("Client QR poster failed"); }
                    finally { setClientQr(false); }
                  }}>
            {clientQr ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Building2 className="w-3.5 h-3.5 mr-1" />}
            Client QR poster
          </Button>

          <Button size="sm" variant="outline" className="h-9 text-xs" disabled={clientQrImg || !client}
                  data-testid="client-qr-image-btn"
                  title={client ? `Download the client QR image for ${client}` : "Select a client first"}
                  onClick={async () => {
                    if (!client) return;
                    setClientQrImg(true);
                    try {
                      const params = new URLSearchParams({ client_name: client });
                      const r = await api.get(`/site-qr/client-qr.png?${params.toString()}`, { responseType: "blob", timeout: 60000 });
                      const u = URL.createObjectURL(r.data);
                      const el = document.createElement("a");
                      el.href = u;
                      el.download = `GBE-Client-QR-${client.replace(/[^a-z0-9]+/gi, "_").slice(0, 40)}.png`;
                      el.click();
                      URL.revokeObjectURL(u);
                    } catch { toast.error("Client QR image failed"); }
                    finally { setClientQrImg(false); }
                  }}>
            {clientQrImg ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <ImageDown className="w-3.5 h-3.5 mr-1" />}
            QR image
          </Button>

          <Button size="sm" variant="outline" className="h-9 text-xs" data-testid="client-posters-batch-btn"
                  title="Generate QR posters for many clients at once (mail-outs)"
                  onClick={() => setBatchOpen(true)}>
            <Users className="w-3.5 h-3.5 mr-1" /> Batch posters
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                {["Rank", "Asset", "Site", "Class", "Cond", "Risk", "Rem. Life", "Priority", "Cost", "Horizon", "Impact", "Decision"].map((c) => (
                  <th key={c} className="text-left font-medium px-4 py-2.5 whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}><td colSpan={12} className="px-4 py-3"><div className="h-5 rounded shimmer" /></td></tr>
                ))
              ) : items.length === 0 ? (
                <tr><td colSpan={12} className="text-center py-10 text-sm text-slate-500">No assets match.</td></tr>
              ) : (
                items.map((a, idx) => (
                  <tr
                    key={a.asset_id || `row-${idx}`}
                    data-testid={TID.capitalAssetRow(a.asset_id)}
                    onClick={() => setSelected(a)}
                    className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3 font-mono text-xs text-slate-500 tabular-nums">
                      #{a.v8_portfolio_rank ? Math.round(a.v8_portfolio_rank) : idx + 1}
                    </td>
                    <td className="px-4 py-3 max-w-[280px]">
                      <div className="text-slate-800 truncate font-medium">{a.asset_name || a.estimated_asset_type}</div>
                      <div className="text-[11px] text-slate-400 truncate">{a.cfas_asset_location || a.estimated_asset_type}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 max-w-[180px] truncate">{a.site_name}</td>
                    <td className="px-4 py-3 text-slate-600">{a.asset_group}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-[11px] font-semibold ${COND_COLOR(a.condition || 0)}`}>
                        {a.condition ?? "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 tabular-nums text-center">{a.risk ?? "—"}</td>
                    <td className="px-4 py-3 text-slate-600 tabular-nums">{a.remaining_life_years ?? "—"} yr</td>
                    <td className="px-4 py-3 tabular-nums font-medium text-slate-800">{Math.round(a.v6_priority_score ?? a.priority_score ?? 0)}</td>
                    <td className="px-4 py-3 tabular-nums font-medium text-slate-800">{fmtCurrency(a.cost)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-medium ${HORIZON_STYLE[a.v8_renewal_status] || ""}`}>
                        {displayStatus(a.v8_renewal_status)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <ConsequenceBadge a={a} testid={`consequence-${a.asset_id}`} />
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => setDecisionAsset(a)} data-testid={`decision-btn-${a.asset_id}`}
                              className="text-left hover:opacity-75 transition-opacity" title="Record a decision">
                        <DecisionBadge status={a.decision_status} decision={a.client_decision} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
            <div>Page {page + 1} of {totalPages}</div>
            <div className="flex gap-2">
              <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="px-3 py-1 border border-slate-200 rounded-md hover:bg-slate-50 disabled:opacity-50">Prev</button>
              <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="px-3 py-1 border border-slate-200 rounded-md hover:bg-slate-50 disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-[560px] overflow-y-auto">
          {selected && <AssetDetailSheet a={selected} />}
        </SheetContent>
      </Sheet>

      <AssetDecisionDialog asset={decisionAsset} onOpenChange={(o) => !o && setDecisionAsset(null)}
        onSaved={(u) => setItems((prev) => prev.map((it) =>
          it.asset_id === u.asset_id ? { ...it, client_decision: u.client_decision, decision_status: u.decision_status } : it))} />

      <ClientPostersDialog open={batchOpen} onOpenChange={setBatchOpen} />
    </>
  );
}

function AssetDetailSheet({ a }) {
  const scoring = [
    { k: "Condition", v: a.condition, max: 5 },
    { k: "Risk", v: a.risk, max: 5 },
    { k: "Utilisation", v: a.utilisation, max: 5 },
    { k: "Environment", v: a.environment, max: 5 },
  ];
  return (
    <>
      <SheetHeader>
        <SheetTitle className="font-display text-slate-900">{a.asset_name || a.estimated_asset_type}</SheetTitle>
        <SheetDescription>
          <span className="font-mono text-xs text-slate-500">{a.v8_portfolio_rank ? `#${Math.round(a.v8_portfolio_rank)} · ` : ""}{a.asset_id}</span>
          <span className={`ml-2 inline-flex px-2 py-0.5 rounded-full border text-[10px] font-medium ${HORIZON_STYLE[a.v8_renewal_status] || ""}`}>
            {displayStatus(a.v8_renewal_status)}
          </span>
          {a.consequence && (
            <span className={`ml-1 inline-flex px-2 py-0.5 rounded-full border text-[10px] font-medium ${(a.consequence_rating ? RATING[a.consequence_rating] : CONSEQ[a.consequence])?.cls || ""}`}>
              {(a.consequence_rating ? RATING[a.consequence_rating] : CONSEQ[a.consequence])?.label}
            </span>
          )}
          <span className="ml-2 inline-flex gap-2 align-middle">
            {a.asset_id && (
              <>
                <button className="text-[10px] text-gbe hover:underline" data-testid="detail-qr-label"
                        onClick={async () => {
                          try {
                            const r = await api.get(`/field/assets/${a.asset_id}/label.pdf`, { responseType: "blob" });
                            const u = URL.createObjectURL(r.data);
                            const el = document.createElement("a"); el.href = u; el.download = `label_${a.asset_id.slice(0, 10)}.pdf`; el.click();
                            URL.revokeObjectURL(u);
                          } catch { /* noop */ }
                        }}>QR label</button>
                <a className="text-[10px] text-gbe hover:underline" href={`/asset/${a.asset_id}`} target="_blank" rel="noopener noreferrer"
                   data-testid="detail-field-page">Field page</a>
              </>
            )}
          </span>
        </SheetDescription>
      </SheetHeader>

      {/* Why this score? — explainable ranking (brief §15) */}
      <div className="mt-4 rounded-md border border-violet-200 bg-violet-50/40 p-3" data-testid="why-score-panel">
        <div className="text-[10px] uppercase tracking-wider text-violet-700 font-semibold">Why this score?</div>
        <p className="text-xs text-slate-700 mt-1">
          Priority {Math.round(a.v6_priority_score ?? 0)}/100 — {[
            (a.condition ?? 0) >= 4 ? "the asset is in poor condition" : (a.condition ?? 0) >= 3 ? "condition is deteriorating" : null,
            (a.risk ?? 0) >= 4 ? "failure would have high consequences" : (a.risk ?? 0) >= 3 ? "failure consequences are elevated" : null,
            (a.v6_life_pressure ?? 0) >= 4 ? "it is approaching the end of its expected life" : null,
            (a.utilisation ?? 0) >= 4 ? "it works under high utilisation" : null,
            (a.environment ?? 0) >= 4 ? "it operates in a harsh environment" : null,
          ].filter(Boolean).join(", ") || "no individual factor is critical; the score reflects the balance of available ratings and replacement cost"}.
          {" "}Factors with no data are excluded and the remaining weights re-balanced.
        </p>
      </div>

      <div className="mt-5">
        <div className="grid grid-cols-2 gap-4">
          <Info label="Client" value={a.client_name} />
          <Info label="Site" value={a.site_name} />
          <Info label="Location" value={a.cfas_asset_location || "—"} />
          <Info label="Asset Class" value={a.asset_group} />
          <Info label="Asset Type" value={a.estimated_asset_type || a.asset_type || "—"} />
          <Info label="Replacement Value" value={fmtCurrency(a.cost)} />
          <Info label="Material (60%)" value={fmtCurrency(a.material_60pct)} />
          <Info label="Labour (40%)" value={fmtCurrency(a.labour_40pct)} />
          <Info label="Install Year" value={a.install_year || "—"} />
          <Info label="Renewal Year" value={a.renewal_year || "—"} />
          <Info label="Remaining Life" value={`${a.remaining_life_years ?? "—"} years`} />
          <Info label="Asset Life" value={`${a.asset_life_years ?? "—"} years`} />
        </div>
      </div>

      <div className="mt-6">
        <h4 className="text-sm font-medium text-slate-900 mb-3">Condition scoring</h4>
        <div className="space-y-2">
          {scoring.map((s) => (
            <div key={s.k}>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-600">{s.k}</span>
                <span className="tabular-nums font-medium text-slate-800">{s.v ?? "—"} / {s.max}</span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full ${s.v >= 4 ? "bg-rose-500" : s.v >= 3 ? "bg-amber-500" : "bg-emerald-500"}`}
                  style={{ width: `${((s.v || 0) / s.max) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-3">
        <Metric label="Priority" value={Math.round(a.v6_priority_score ?? a.priority_score ?? 0)} />
        <Metric label="Health" value={Math.round(a.health_score ?? 0)} />
        <Metric label="Confidence" value={Math.round(a.v8_confidence_score ?? 0)} />
      </div>

      <div className="mt-6 space-y-3 text-sm">
        <Block label="Recommended action" value={a.v8_suggested_action || a.v6_recommended_action || a.recommended_action} />
        <Block label="Recommendation basis" value={a.v6_recommendation_basis} />
        <Block label="Consequence if deferred" value={a.consequence__deferred_risk} />
        <Block label="Suggested replacement" value={
          <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-medium ${
            a.suggested_replacement === "Yes" ? "bg-rose-50 text-rose-700 border-rose-200"
            : a.suggested_replacement === "Watch" ? "bg-amber-50 text-amber-700 border-amber-200"
            : "bg-emerald-50 text-emerald-700 border-emerald-200"
          }`}>{a.suggested_replacement}</span>
        } />
        <Block label="Technician validation required" value={a.technician_validation_required} />
        <Block label="Data confidence" value={a.data_confidence} />
      </div>
    </>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm text-slate-900 mt-0.5 break-words">{value ?? "—"}</div>
    </div>
  );
}
function Metric({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-md p-3 text-center">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 font-display text-xl font-semibold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}
function Block({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5">{value}</div>
    </div>
  );
}
